import { useEffect, useState, type ReactNode } from "react";
import {
  Box,
  Column,
  IconButton,
  CloseIcon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Portal,
  Text,
  type BoxProps,
} from "@hightouch/htui";
import { motion } from "motion/react";
import { AttachmentMedia } from "src/components/llm/components/attachment-media";
import {
  composerImageSrc,
  isVideo,
  resolveAttachmentMimeType,
  base64ToBlobUrl,
  type ComposerFile,
} from "src/components/llm/utils/file-processing";
import { truncateMiddle } from "src/components/llm/utils/truncate-middle";
const MotionBox = motion.create(Box);
/** Rotation for each pill alternates -3° / +3° */
const getRotation = (index: number) => (index % 2 === 0 ? -3 : 3);

const POPOVER_CONTENT_STYLES: BoxProps = {
  p: 1,
  borderRadius: "lg",
  overflow: "hidden",
  border: "1px solid",
  borderColor: "gray.700",
  bg: "base.containerBackground",
  outline: "2px solid",
  outlineColor: "base.containerBackground",
  boxShadow: "darkPopover",
  width: "auto",
};

export const BasePill = ({
  thumbnail,
  popoverPreview,
  label,
  secondaryLabel,
  index,
  isRowHovered,
  isPopoverOpen,
  onPillEnter,
  onPillLeave,
  onRemove,
  onImageClick,
  bg,
}: {
  thumbnail: ReactNode;
  popoverPreview: ReactNode;
  label: string;
  /** A second line under the name, such as the folder the file came from. */
  secondaryLabel?: string;
  index: number;
  isRowHovered: boolean;
  isPopoverOpen?: boolean;
  onPillEnter?: () => void;
  onPillLeave?: () => void;
  onRemove?: () => void;
  onImageClick?: () => void;
  bg?: string;
}) => {
  const rotation = getRotation(index);

  return (
    <MotionBox
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      animate={{
        marginRight: isRowHovered ? "4px" : "-4px",
        rotate: isRowHovered ? 0 : rotation,
      }}
      transition={{ duration: 0.2, ease: [0.645, 0.045, 0.355, 1] }}
    >
      <Popover
        isOpen={isPopoverOpen ?? false}
        placement="top"
        isLazy
        lazyBehavior="unmount"
      >
        <PopoverTrigger>
          <Box
            data-group
            position="relative"
            cursor={onImageClick ? "zoom-in" : "default"}
            onMouseEnter={onPillEnter}
            onMouseLeave={onPillLeave}
          >
            <Box
              as="button"
              type="button"
              aria-label={`Preview ${label}`}
              onClick={onImageClick ?? onPillEnter}
              width="32px"
              height="32px"
              borderRadius="md"
              border="1px solid"
              borderColor="white"
              overflow="hidden"
              position="relative"
              boxShadow="sm"
              bg={bg}
            >
              {thumbnail}
              {onImageClick && (
                <Box
                  position="absolute"
                  inset={0}
                  bg="blackAlpha.16"
                  opacity={0}
                  transitionProperty="opacity"
                  transitionDuration="fast"
                  transitionTimingFunction="easeOutQuad"
                  _groupHover={{ opacity: 1 }}
                  pointerEvents="none"
                />
              )}
            </Box>
            {onRemove && (
              <Box
                position="absolute"
                top={-2}
                right={-2}
                zIndex={1}
                opacity={isRowHovered ? 1 : 0}
                // Keyboard focus reveals removal independently of pointer hover.
                sx={{ "&:has(:focus-visible)": { opacity: 1 } }}
              >
                <IconButton
                  icon={CloseIcon}
                  size="2xs"
                  shape="circle"
                  variant="secondary"
                  aria-label={`Remove ${label}`}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                  }}
                />
              </Box>
            )}
          </Box>
        </PopoverTrigger>
        <Portal>
          <PopoverContent {...POPOVER_CONTENT_STYLES}>
            <Column gap={1}>
              <Box
                boxSize="200px"
                borderRadius="md"
                overflow="hidden"
                flexShrink={0}
                bg={bg}
              >
                {popoverPreview}
              </Box>
              <Column
                px="2px"
                pb={1}
                // The preview's width, so a long folder path wraps rather than widening the popover.
                maxWidth="200px"
              >
                <Text size="sm" fontWeight="medium" color="white">
                  {truncateMiddle(label, 18)}
                </Text>
                {secondaryLabel && (
                  <Text size="sm" color="whiteAlpha.80" wordBreak="break-word">
                    {secondaryLabel}
                  </Text>
                )}
              </Column>
            </Column>
          </PopoverContent>
        </Portal>
      </Popover>
    </MotionBox>
  );
};

export const ComposerFilePill = ({
  file,
  index,
  isRowHovered,
  isPopoverOpen,
  onPillEnter,
  onPillLeave,
  onRemove,
  onImageClick,
}: {
  file: ComposerFile;
  index: number;
  isRowHovered: boolean;
  isPopoverOpen?: boolean;
  onPillEnter?: () => void;
  onPillLeave?: () => void;
  onRemove?: () => void;
  onImageClick?: () => void;
}) => {
  const imageSrc = composerImageSrc(file);
  const isVideoFile = isVideo(file.mimeType, file.fileName);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const { previewUrl } = file;

  // An uploaded attachment already has an object URL owned by the store, so the
  // poster frame can point straight at it. Only the legacy base64 path has to
  // build one here: large videos are unreliable as data URIs, and decoding
  // base64 into a Blob is a side effect (it allocates and can block on a large
  // payload), so build it in an effect rather than during render, and revoke it
  // when the pill unmounts or the file changes to avoid leaking.
  useEffect(() => {
    if (!isVideoFile) {
      setVideoSrc(null);
      return;
    }
    if (previewUrl) {
      setVideoSrc(previewUrl);
      return;
    }
    if (!file.base64Data) {
      setVideoSrc(null);
      return;
    }
    let url: string | null = null;
    try {
      url = base64ToBlobUrl(
        file.base64Data,
        resolveAttachmentMimeType(file.mimeType, file.fileName),
      );
    } catch {
      // Malformed base64: leave the source null so the file-icon fallback renders.
      setVideoSrc(null);
      return;
    }
    setVideoSrc(url);
    return () => {
      URL.revokeObjectURL(url);
      setVideoSrc(null);
    };
  }, [isVideoFile, previewUrl, file.base64Data, file.mimeType, file.fileName]);

  const renderMedia = (variant: "thumbnail" | "preview") => (
    <AttachmentMedia
      fileName={file.fileName}
      mimeType={file.mimeType}
      src={imageSrc ?? videoSrc}
      variant={variant}
    />
  );

  const fileThumbnail = renderMedia("thumbnail");
  const filePreview = renderMedia("preview");

  return (
    <BasePill
      thumbnail={fileThumbnail}
      popoverPreview={filePreview}
      label={file.fileName}
      secondaryLabel={file.folderPath ?? undefined}
      index={index}
      isRowHovered={isRowHovered}
      isPopoverOpen={isPopoverOpen}
      onPillEnter={onPillEnter}
      onPillLeave={onPillLeave}
      onRemove={onRemove}
      onImageClick={onImageClick}
    />
  );
};
